import { useMemo, useState } from 'react';

import { InfoPopover } from '@/components/common/InfoPopover';
import { SectionTitle } from '@/components/common/SectionTitle';
import { DEFAULT_PERSONA, policyName } from '@/features/decisions/config/personaContext';
import type { FcfEntitlementBar } from '@/features/decisions/decisions.api.types';
import {
  fmtDaysWithSign,
  fmtMoneyM,
  fmtMoneyMWithSign,
  fmtPointsWithSign,
  type DeltaBuildup,
} from '@/features/decisions/utils/fcfFormat';
import { computeOcf, computeOcfDeltas } from '@/features/decisions/utils/ocf';

interface FcfBuildupProps {
  refBar: FcfEntitlementBar | undefined;
  headlineCmp: FcfEntitlementBar | undefined;
  deltas: DeltaBuildup | null;
  // Fixed lost-sales assumption shared with the chart so they reconcile. The
  // interactive slider was removed (Max review); the value is now a constant 0%.
  lostSalesPct: number;
  // Multiplier applied to all financial values (OCF, EBIT, Cash In, COGS, NWC).
  chartMultiplier?: number;
}

type OcfTab = 'change' | 'absolute';

// Operating Cash Flow build-up. Two tabbed views — Change in OCF (Optimized vs
// Incumbent, default) and absolute OCF per scenario — over a shared lost-sales
// slider that haircuts backlog-recovered revenue. EBIT / COGS / ΔNWC depend on
// the backend `model_cogs` field (#9) and show "—" until it lands; Cash In and
// the OCF total resolve from the already-served cash terms.
export function FcfBuildup({
  refBar,
  headlineCmp,
  deltas,
  lostSalesPct,
  chartMultiplier = DEFAULT_PERSONA.chartMultiplier,
}: FcfBuildupProps) {
  const [tab, setTab] = useState<OcfTab>('change');

  const ocfDeltas = useMemo(() => {
    const d = computeOcfDeltas(refBar, headlineCmp, lostSalesPct);
    const s = chartMultiplier;
    return {
      ebit: d.ebit != null ? d.ebit * s : null,
      cashIn: d.cashIn != null ? d.cashIn * s : null,
      cogs: d.cogs != null ? d.cogs * s : null,
      changeInNwc: d.changeInNwc != null ? d.changeInNwc * s : null,
      ocf: d.ocf != null ? d.ocf * s : null,
    };
  }, [refBar, headlineCmp, lostSalesPct, chartMultiplier]);

  const ocfRef = useMemo(() => {
    const r = computeOcf(refBar, lostSalesPct);
    const s = chartMultiplier;
    return {
      ebit: r.ebit != null ? r.ebit * s : null,
      cashIn: r.cashIn != null ? r.cashIn * s : null,
      cogs: r.cogs != null ? r.cogs * s : null,
      changeInNwc: r.changeInNwc != null ? r.changeInNwc * s : null,
      ocf: r.ocf != null ? r.ocf * s : null,
    };
  }, [refBar, lostSalesPct, chartMultiplier]);

  const ocfCmp = useMemo(() => {
    const c = computeOcf(headlineCmp, lostSalesPct);
    const s = chartMultiplier;
    return {
      ebit: c.ebit != null ? c.ebit * s : null,
      cashIn: c.cashIn != null ? c.cashIn * s : null,
      cogs: c.cogs != null ? c.cogs * s : null,
      changeInNwc: c.changeInNwc != null ? c.changeInNwc * s : null,
      ocf: c.ocf != null ? c.ocf * s : null,
    };
  }, [headlineCmp, lostSalesPct, chartMultiplier]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <SectionTitle>Operating Cash Flow Build Up</SectionTitle>
          {headlineCmp && (
            <p className="mt-1 text-xs text-gray-500" title={headlineCmp.experiment_id}>
              {policyName(headlineCmp.experiment_id)} versus Incumbent
            </p>
          )}
        </div>
        <InfoPopover widthClass="w-96">
          <p className="font-semibold text-gray-800">How this is calculated</p>
          <p className="mt-1.5">Per scenario, summed over the selected period (SKUs common to all experiments).</p>
          <dl className="mt-2 space-y-1.5 font-mono text-[11px] leading-relaxed text-gray-700">
            <div>
              <dt className="font-semibold text-gray-800">Cash In</dt>
              <dd>Revenue (Demand Fulfilled) + (1 − Lost Sales %) × Revenue (Backlog Fulfilled)</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-800">COGS</dt>
              <dd>Cost Per Unit × Units Fulfilled</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-800">EBIT (Operating Income)</dt>
              <dd>Cash In − COGS</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-800">Total Production Cost</dt>
              <dd>Order Placed Total Cost</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-800">Δ Net Working Capital</dt>
              <dd>Total Production Cost − COGS</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-800">Operating Cash Flow</dt>
              <dd>Cash In − Total Production Cost (= EBIT − Δ NWC)</dd>
            </div>
          </dl>
          <p className="mt-2">
            <span className="font-medium text-gray-800">Δ Rows</span> = Optimized − Incumbent.{' '}
            <span className="font-medium text-gray-800">Lost Sales %</span> reduces only Revenue (Backlog Fulfilled),
            lowering Cash In, EBIT and Operating Cash Flow; Total Production Cost, COGS and Δ Net Working Capital are
            unaffected.
          </p>
        </InfoPopover>
      </div>

      {/* Tabs: Change in OCF (P0, default) vs absolute Operating Cash Flow (P1) */}
      <div className="mt-3 flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 text-xs">
        {(
          [
            { id: 'change', label: 'Δ OCF' },
            { id: 'absolute', label: 'Operating Cash Flow' },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex-1 rounded-md px-2 py-1 font-medium transition-colors ${
              tab === t.id ? 'bg-white text-[#274483] shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'change' ? (
        <div className="mt-3">
          <div className="mt-1 divide-y divide-gray-100 text-sm">
            <DeltaRow
              label="Δ EBIT (Operating Income)"
              value={ocfDeltas.ebit}
              betterWhen="higher"
              title="Cash In − COGS"
            />
            <DeltaRow
              label="Δ Cash In"
              value={ocfDeltas.cashIn}
              betterWhen="higher"
              indent
              title="Cash from sales, net of the lost-sales haircut"
            />
            <DeltaRow label="Δ COGS" value={ocfDeltas.cogs} betterWhen="lower" indent title="Cost of units fulfilled" />
            <DeltaRow
              label="Δ Net Working Capital"
              value={ocfDeltas.changeInNwc}
              betterWhen="lower"
              title="Production cost − COGS; Δ EBIT − Δ Net Working Capital = Δ OCF"
            />
          </div>
          <TotalRow label="Δ Operating Cash Flow" value={ocfDeltas.ocf} betterWhen="higher" />

          <div className="mt-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Operational Metrics</div>
            <div className="mt-1 divide-y divide-gray-100 text-sm">
              <DeltaMetricRow
                label="Δ Days of Supply"
                value={deltas?.days_of_supply_change}
                text={fmtDaysWithSign(deltas?.days_of_supply_change)}
                betterWhen="lower"
              />
              <DeltaMetricRow
                label="Δ Available in Full"
                value={deltas?.available_in_full_per_period_change}
                text={fmtPointsWithSign(deltas?.available_in_full_per_period_change)}
                betterWhen="higher"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-3">
          <div className="grid grid-cols-[1fr_auto_auto] items-baseline gap-x-3 text-sm">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Line</span>
            <span className="text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Incumbent
            </span>
            <span className="text-right text-[11px] font-semibold uppercase tracking-wide text-[#274483]">
              {headlineCmp ? policyName(headlineCmp.experiment_id) : 'Optimized'}
            </span>
            <AbsRow label="EBIT (Operating Income)" refValue={ocfRef.ebit} cmpValue={ocfCmp.ebit} />
            <AbsRow label="Cash In" refValue={ocfRef.cashIn} cmpValue={ocfCmp.cashIn} indent />
            <AbsRow label="COGS" refValue={ocfRef.cogs} cmpValue={ocfCmp.cogs} indent />
            <AbsRow label="Change in Net Working Capital" refValue={ocfRef.changeInNwc} cmpValue={ocfCmp.changeInNwc} />
            {/* Full-width separator keeps the total in the SAME grid so its columns
                line up with the header/rows. */}
            <div className="col-span-3 mt-2 border-t-2 border-gray-200" />
            <span className="pt-2 text-base font-semibold text-gray-900">Operating Cash Flow</span>
            <span className="pt-2 text-right text-base font-bold tabular-nums text-gray-900">
              {fmtMoneyM(ocfRef.ocf)}
            </span>
            <span className="pt-2 text-right text-base font-bold tabular-nums text-[#274483]">
              {fmtMoneyM(ocfCmp.ocf)}
            </span>
            <div className="col-span-3 mt-4 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Operational Metrics
            </div>
            <AbsTextRow
              label="Days of Supply"
              refText={fmtDaysAbs(refBar?.days_of_supply)}
              cmpText={fmtDaysAbs(headlineCmp?.days_of_supply)}
            />
            <AbsTextRow
              label="Available in Full"
              refText={fmtPctAbs(refBar?.available_in_full_per_period)}
              cmpText={fmtPctAbs(headlineCmp?.available_in_full_per_period)}
            />
          </div>
        </div>
      )}

      {!refBar?.model_cogs && !headlineCmp?.model_cogs && (
        <p className="mt-2 text-[10px] text-gray-400">
          COGS, EBIT and Δ Net Working Capital need the backend model_cogs field (#9).
        </p>
      )}
    </div>
  );
}

// Tone by effect on OCF: green when the change improves it (higher cash/EBIT/OCF,
// lower cost/NWC), red otherwise, gray when there's no data.
function tone(value: number | null | undefined, betterWhen: 'higher' | 'lower'): string {
  if (value == null || value === 0) return 'text-gray-400';
  const improved = betterWhen === 'higher' ? value > 0 : value < 0;
  return improved ? 'text-emerald-600' : 'text-rose-600';
}

function DeltaRow({
  label,
  value,
  betterWhen,
  title,
  indent = false,
}: {
  label: string;
  value: number | null | undefined;
  betterWhen: 'higher' | 'lower';
  title?: string;
  indent?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-2" title={title}>
      <span className={indent ? 'pl-4 text-gray-500' : 'text-gray-700'}>{label}</span>
      <span className={`font-semibold tabular-nums ${tone(value, betterWhen)}`}>{fmtMoneyMWithSign(value)}</span>
    </div>
  );
}

function TotalRow({
  label,
  value,
  betterWhen,
}: {
  label: string;
  value: number | null | undefined;
  betterWhen: 'higher' | 'lower';
}) {
  return (
    <div className="mt-2 flex items-center justify-between gap-3 border-t-2 border-gray-200 pt-2">
      <span className="text-base font-semibold text-gray-900">{label}</span>
      <span className={`text-lg font-bold tabular-nums ${tone(value, betterWhen)}`}>{fmtMoneyMWithSign(value)}</span>
    </div>
  );
}

function AbsRow({
  label,
  refValue,
  cmpValue,
  indent = false,
}: {
  label: string;
  refValue: number | null;
  cmpValue: number | null;
  indent?: boolean;
}) {
  return (
    <>
      <span className={`py-2 ${indent ? 'pl-4 text-gray-500' : 'text-gray-700'}`}>{label}</span>
      <span className="py-2 text-right tabular-nums text-gray-800">{fmtMoneyM(refValue)}</span>
      <span className="py-2 text-right font-medium tabular-nums text-gray-900">{fmtMoneyM(cmpValue)}</span>
    </>
  );
}

const fmtDaysAbs = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(1)} days`);
const fmtPctAbs = (v: number | null | undefined) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);

// Absolute per-scenario row (pre-formatted text), aligned to the same grid columns.
function AbsTextRow({ label, refText, cmpText }: { label: string; refText: string; cmpText: string }) {
  return (
    <>
      <span className="py-2 text-gray-700">{label}</span>
      <span className="py-2 text-right tabular-nums text-gray-800">{refText}</span>
      <span className="py-2 text-right font-medium tabular-nums text-gray-900">{cmpText}</span>
    </>
  );
}

// One slider-independent delta row (working capital / service level). Green when
// the change improves, red otherwise, gray when there is no data.
function DeltaMetricRow({
  label,
  value,
  text,
  betterWhen,
}: {
  label: string;
  value: number | null | undefined;
  text: string;
  betterWhen: 'higher' | 'lower';
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <span className="text-gray-700">{label}</span>
      <span className={`font-semibold tabular-nums ${tone(value, betterWhen)}`}>{text}</span>
    </div>
  );
}
